import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { LoadPanel, Popup } from "devextreme-react";

import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
// The shared error-message ladder (Architecture.md rule 16). It already covers everything a local
// helper would: axios errors with a response body, PascalCase or camelCase Message, string bodies, and
// the timeout case where error.response is undefined.
import { getApiErrorMessage } from "../../../../../common/concurrency";
import { useCommittedFilterRows } from "../../../../../common/reportFilters";
import { DisableEnableControls } from "../../../../../common/Utils";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import CommonReport from "../../../../../reports/components/CommonReport";
import OperationResultGrid from "../../../charges/common/OperationResultGrid";
import { ParametersLayout } from "./Layout";
import { PREVIEW_KEY_EXPR, previewColumns } from "./PreviewGridColumns";
import "./purgeNeverUsedRecords.css";
import {
    BACKUP_WARNING_TEXT,
    BUTTON,
    DEFAULT_LAYOUT_NAME,
    FIELD,
    HELP_TEXT,
    LAYOUT_ID,
    LAYOUT_NAME,
    PAGE_TITLE,
    PREVIEW_GRID_KEY,
    PREVIEW_TITLE,
    REPORT,
    REPORT_LOAD_TIMEOUT_MS,
    REQUEST_TIMEOUT_MS,
    resolvePurgeLevel,
} from "./constants";

const BUSY = Object.freeze({ IDLE: "idle", PREVIEWING: "previewing", PURGING: "purging" });

/**
 * Puts a class on the Preview popup's WRAPPER so ../../../charges/common/operationScreen.css can reach
 * `.dx-popup-content` — the element DevExtreme sizes, and the one that has to become a flex column for
 * the grid to fill it. There is no other hook: the popup renders into an overlay outside this
 * component's DOM subtree.
 *
 * Module-level so the object identity is stable; an inline literal would be a new object every render
 * and make the Popup re-apply it.
 */
const PREVIEW_POPUP_ATTR = { class: "op-preview-popup" };

/**
 * The empty row set, as ONE module-level array.
 *
 * A fresh `[]` on every clear would be a new prop identity each time, which defeats the memo on
 * OperationResultGrid — and a re-render there rebuilds the grid's toolbar template, remounting
 * GridLayoutManager and firing another GetGridLayouts. Not frozen: DevExtreme is free to read this as a
 * dataSource and Object.freeze would turn any internal in-place work into a throw.
 */
const NO_PREVIEW_ROWS = [];

/**
 * Did the user actually enter any filter criteria?
 *
 * Tested against the COMMITTED rows, i.e. what `readFilterRows` returns, so a value typed without
 * tabbing out is not mistaken for an empty filter — on this screen that mistake would ask the user to
 * confirm deleting EVERY eligible record. Mirrors the same helper on Generate Repeating Misc Charges.
 *
 * Only `from` counts: a row carrying nothing but a sort produces no predicate, so the server would see
 * an empty WHERE and purge company-wide.
 */
const hasFilterCriteria = (rows) =>
    (rows || []).some((row) => row && row.from !== null && row.from !== undefined && row.from !== "");

/**
 * Is the checkbox combination one the run can act on? True when valid.
 *
 * Mirrors PurgeNeverUsedRecordsService.ValidateRequest exactly: at least one box ticked, and the
 * cascade intact — Item implies Lot and Sublot, Lot implies Sublot. Checked here so an unusable
 * combination costs no round trip; the server keeps the identical check, because unlike the two
 * confirmations this is validation and a direct API call must not get past it.
 *
 * Only the "nothing ticked" case is actually reachable through the UI — unticking Item, or Lot, or a
 * lone Sublot clears everything — since the cascade handler maintains the rest. The broken-cascade arms
 * are here so the two implementations stay recognisably the same rule.
 */
const isValidSelection = (fields) => {
    const items = fields[FIELD.PurgeItems];
    const lots = fields[FIELD.PurgeLots];
    const sublots = fields[FIELD.PurgeSublots];

    if (!items && !lots && !sublots) {
        return false;
    }

    if (items && (!lots || !sublots)) {
        return false;
    }

    return !(lots && !sublots);
};

/**
 * A function, not a module-level constant: Reset must hand back a fresh object, and a shared one would
 * be mutated across resets.
 *
 * Defaults are the DFM's: Item unchecked, Lot and Sublot both checked (cbLot and cbSublot carry
 * State = cbChecked), and Sublot arrives disabled because Lot is checked.
 */
const initialFormFields = () => ({
    [FIELD.PurgeItems]: false,
    [FIELD.PurgeLots]: true,
    [FIELD.PurgeSublots]: true,
});

/**
 * WM "Purge Never Used Items, Lots and Sublots" (legacy WMD0120).
 *
 * Start permanently deletes IMITEM, IMLOT or IMSUBLOT rows that have never been used, and reports how
 * many went. Preview runs the same filter through the same FROM/WHERE as a read-only SELECT, so it shows
 * exactly what Start would delete.
 *
 * This is an OPERATION screen: no CustomStore, no <Editing>, no per-row read-modify-write, so
 * Architecture.md rules 4-6, 12 and 14-16 (including optimistic concurrency) do not apply.
 *
 * The legacy Help and Exit buttons are dropped: Help opened a Delphi context topic and Exit closed the
 * MDI child, neither of which has a web equivalent.
 */
const PurgeNeverUsedRecords = (props) => {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const formRefs = useRef({});
    const reportRef = useRef(null);
    const previewGrid = useRef(null);

    const [formFields, setFormFields] = useState(initialFormFields);
    const [filterWhereInfoList, setFilterWhereInfoList] = useState([]);
    const [busy, setBusy] = useState(BUSY.IDLE);

    /**
     * One object rather than three useStates. React 17 does not batch setState across an await, so
     * three separate updates after the fetch meant three extra renders of the page — and each one
     * rebuilt the grid's toolbar template, remounting GridLayoutManager and firing another
     * GetGridLayouts request.
     */
    const [preview, setPreview] = useState({ rows: NO_PREVIEW_ROWS, visible: false, hasRun: false });
    const [previewLayout, setPreviewLayout] = useState(DEFAULT_LAYOUT_NAME);

    /**
     * Whether the Preview popup exists in the tree at all. False until the user first presses Preview,
     * and back to false whenever the purge level changes.
     *
     * This is what keeps a checkbox click down to a single request. The popup contains
     * OperationResultGrid, which on mount runs GetGridLayouts and its StateStoring customLoad, and on a
     * column-set change persists the new state with SaveUpdateGridLayout. None of that is work the user
     * asked for while they are ticking a checkbox — so the grid simply is not there to do it. It mounts
     * when Preview is pressed, which is the moment those calls are legitimate.
     *
     * Set one render BEFORE `visible` flips, deliberately: DevExtreme captures a Popup's content when
     * the popup is created, and this codebase has been bitten by a `{state && …}` guard inside a Popup
     * capturing an empty template that never filled (see the sibling screens' result dialogs). Gating
     * the whole Popup element, and mounting it in an earlier commit than the one that shows it, avoids
     * that shape entirely.
     */
    const [previewMounted, setPreviewMounted] = useState(false);

    /**
     * True while GetReport is in flight for the current level — on first mount, on every level change,
     * and on Reset. Starts true because the first render already triggers the fetch.
     */
    const [reportLoading, setReportLoading] = useState(true);
    const reportLoadTimer = useRef(null);

    const isBusy = busy !== BUSY.IDLE || reportLoading;

    /**
     * A purge or a preview outranks the catalogue fetch: both can only start once the fields are loaded,
     * so if one is running that is what the user is waiting on.
     */
    const loadPanelMessage = busy === BUSY.PURGING
        ? MESSAGES.PURGING_NEVER_USED_RECORDS
        : busy === BUSY.PREVIEWING
            ? MESSAGES.PREVIEWING_NEVER_USED_RECORDS
            : MESSAGES.PURGE_LOADING_FIELDS;

    // The level implied by the checkboxes right now. Resolved with the same precedence as the server's
    // ResolveLevel and the Delphi MainAction, so the catalogue on screen is the one the run will use.
    const purgeLevel = resolvePurgeLevel(formFields);

    /**
     * The level the rest of the screen actually follows — `purgeLevel` once it has stopped moving.
     *
     * <b>Why this is not just purgeLevel.</b> One checkbox click produces TWO state writes, and in
     * React 17 they are not batched: DevExtreme fires onValueChanged outside React's synthetic event
     * system, so each setState renders synchronously on its own. FormField writes the clicked field
     * first (FormField.js:340) and only then calls our handler, which applies the cascade. Unticking
     * Purge Lot therefore passes through {Item 0, Lot 0, Sublot 1} — which resolves to Sublot — before
     * landing on {0,0,0}, which resolves to Lot.
     *
     * Feeding that straight into the <CommonReport> key made both renders remount it, so a single click
     * fetched the Sublot catalogue and then the Lot catalogue: two GetReports for a click whose settled
     * level had not changed at all. Deferring by a tick collapses the pair, and because setState bails
     * out when the value is unchanged, a click that ends where it started now costs nothing.
     *
     * The timeout is 0, not a debounce: the two renders are already back to back, so this only has to
     * outlast the current task. The cleanup cancels the pending update when the second render arrives,
     * which is what discards the transient value.
     */
    const [reportLevel, setReportLevel] = useState(purgeLevel);

    useEffect(() => {
        if (reportLevel === purgeLevel) {
            return undefined;
        }

        const handle = setTimeout(() => setReportLevel(purgeLevel), 0);

        return () => clearTimeout(handle);
    }, [purgeLevel, reportLevel]);

    const report = REPORT[reportLevel];

    const postOptions = useCallback(
        () => ({
            headers: {
                "Content-Type": "application/json",
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
            timeout: REQUEST_TIMEOUT_MS,
        }),
        [accessToken]
    );

    const clearReportLoadTimer = useCallback(() => {
        if (reportLoadTimer.current) {
            clearTimeout(reportLoadTimer.current);
            reportLoadTimer.current = null;
        }
    }, []);

    /**
     * Raises the load panel for a GetReport that is about to start, and arms a deadline.
     *
     * The deadline is not belt-and-braces, it is required. CommonReport.loadReport's only error
     * handling is `.catch((err) => console.log(...))` (CommonReport.js:555) — it does NOT call
     * onReportStateChange on failure. So a GetReport that 500s or times out would otherwise leave this
     * panel up forever, with shading on, locking the whole page behind a modal overlay. Better to drop
     * the panel and say so.
     *
     * The right fix is for CommonReport to surface the failure; that is shared code serving every
     * report screen, so it is a separate change rather than something to smuggle in here.
     */
    const beginReportLoad = useCallback(() => {
        clearReportLoadTimer();
        setReportLoading(true);

        reportLoadTimer.current = setTimeout(() => {
            reportLoadTimer.current = null;
            setReportLoading(false);
            notify({ message: MESSAGES.PURGE_FIELDS_LOAD_FAILED, width: 450 }, "error", 5000);
        }, REPORT_LOAD_TIMEOUT_MS);
    }, [clearReportLoadTimer]);

    /**
     * CommonReport fires this once per mount, from loadReport's .then. It stays correct because the
     * editable filter grid is bound to an ArrayStore wrapping this same array instance, which cell edits
     * mutate in place — so the reference captured here stays live. Do not clone it per render.
     *
     * Also the one signal that GetReport finished, so it is what lowers the load panel.
     */
    const handleReportStateChange = useCallback((reportState) => {
        clearReportLoadTimer();
        setReportLoading(false);
        setFilterWhereInfoList(reportState?.filterWhereInfoList ?? []);
    }, [clearReportLoadTimer]);

    // Arms the deadline for the fetch the FIRST render already kicked off (reportLoading starts true, so
    // the panel is already showing; this only adds the timeout), and disarms it on unmount so a pending
    // timer cannot fire setState against a gone component.
    useEffect(() => {
        beginReportLoad();

        return clearReportLoadTimer;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Preview and Start send the identical body — the three checkboxes and the filter — so neither can
     * end up acting on a different row set than the other showed.
     */
    const buildRequest = useCallback(
        (rows) => ({
            [FIELD.PurgeItems]: formFields[FIELD.PurgeItems],
            [FIELD.PurgeLots]: formFields[FIELD.PurgeLots],
            [FIELD.PurgeSublots]: formFields[FIELD.PurgeSublots],
            FilterWhereInfoList: rows || [],
        }),
        [formFields]
    );

    /**
     * Commits any in-progress filter cell before reading the rows — see common/reportFilters.js for why
     * the onReportStateChange array is the wrong source at submit time.
     *
     * Load-bearing on THIS screen specifically: a filter the user typed but has not tabbed out of would
     * otherwise read as empty, which makes Start ask "no filter criteria — delete EVERYTHING?" and, if
     * answered Yes, purge far more than was asked for.
     */
    const readFilterRows = useCommittedFilterRows(reportRef, filterWhereInfoList);

    // Stable identity: an inline arrow here would change on every render and, via the Popup, keep the
    // grid subtree re-rendering.
    const hidePreview = useCallback(() => {
        setPreview((previous) => ({ ...previous, visible: false }));
    }, []);

    /**
     * The Delphi checkbox cascade (cbItemClick / cbLotClick / cbSublotClick), which is really a
     * three-way level selector:
     *   - Item checked   -> Lot and Sublot forced on and disabled.
     *   - Item unchecked -> Lot and Sublot cleared and re-enabled.
     *   - Lot checked    -> Sublot forced on and disabled.
     *   - Lot unchecked  -> Sublot cleared and re-enabled.
     *
     * DisableEnableControls takes `disabled`, not `enabled`, hence the direct (non-negated) argument.
     */
    const applyCascadeEnablement = useCallback((fields) => {
        const itemChecked = fields[FIELD.PurgeItems];
        DisableEnableControls(FIELD.PurgeLots, itemChecked, formRefs);
        DisableEnableControls(FIELD.PurgeSublots, itemChecked || fields[FIELD.PurgeLots], formRefs);
    }, []);

    /**
     * Switching level throws the filter away, exactly as the Delphi does — every checkbox click called
     * `wbPurgeUnusedItems.DataSet.Close; Reset;` and repointed the builder at another dataset. The
     * remount is driven by the `key` on <CommonReport>; this only has to clear what the page itself
     * holds, since a preview of the previous level's rows would be actively misleading.
     */
    const clearLevelDependentState = useCallback(() => {
        setPreview({ rows: NO_PREVIEW_ROWS, visible: false, hasRun: false });
        // Unmounts the grid, so the level's new column set cannot trigger GetGridLayouts or, worse, a
        // SaveUpdateGridLayout write for a grid the user never opened. It comes back on the next Preview.
        setPreviewMounted(false);
        // previewLayout is deliberately NOT reset here. OperationResultGrid memoises its StateStoring
        // customLoad/customSave on currentLayout, so changing it re-runs customLoad and fires another
        // GetGridLayout. It does not need resetting either: the grid has just been unmounted, and each
        // level has its own layout key, so the next mount loads that level's own saved layout by name.
    }, []);

    /**
     * The Delphi checkbox cascade (cbItemClick / cbLotClick / cbSublotClick), which is really a
     * three-way level selector:
     *   - Item checked   -> Lot and Sublot forced on.
     *   - Item unchecked -> Lot and Sublot cleared.
     *   - Lot checked    -> Sublot forced on; Lot unchecked -> Sublot and Item cleared.
     *
     * <b>Must use the functional updater, and must have NO dependencies.</b> FormField memoises its own
     * handleFieldChange with an EMPTY dependency array (FormField.js:339-347), so it captures whichever
     * handleFieldValueChange it was given on the FIRST render and calls that one forever. A handler that
     * closed over `formFields` would therefore compute every cascade from the state as it was at mount,
     * silently discarding the user's later clicks. Reading `previous` from the updater sidesteps that
     * entirely, and keeping the deps empty means the captured handler is never a stale one.
     *
     * Note FormField has already queued its own `{...prev, [name]: value}` for this field before calling
     * us, so `previous` arrives with the clicked value applied; re-applying it here is harmless and
     * keeps the function readable on its own terms.
     *
     * The level-change side effects deliberately live in the effect below, not here: a side effect does
     * not belong in an updater React is free to run more than once.
     */
    const handleFieldChange = useCallback((name, value) => {
        if (name !== FIELD.PurgeItems && name !== FIELD.PurgeLots && name !== FIELD.PurgeSublots) {
            return;
        }

        setFormFields((previous) => {
            const next = { ...previous, [name]: value };

            if (name === FIELD.PurgeItems) {
                next[FIELD.PurgeLots] = value;
                next[FIELD.PurgeSublots] = value;
            } else if (name === FIELD.PurgeLots) {
                next[FIELD.PurgeSublots] = value;
                // Unchecking Lot while Item is set would leave the cascade broken and the server would
                // reject it; Item owns the other two, so clear it too.
                if (!value) {
                    next[FIELD.PurgeItems] = false;
                }
            } else if (!value) {
                // Sublot is disabled whenever Lot or Item is checked, so an unchecking click can only
                // arrive when Sublot alone was selected. Mirrors cbSublotClick's own guard.
                next[FIELD.PurgeItems] = false;
                next[FIELD.PurgeLots] = false;
            }

            return next;
        });
    }, []);

    /**
     * Everything that has to happen when the purge LEVEL changes — which is what a checkbox click means
     * in practice, since the level is derived from the three boxes.
     *
     * Driven off the resolved level rather than from inside the click handler, so it fires exactly once
     * per real transition and cannot be fooled by a stale closure. The level is also exactly what the
     * `key` on <CommonReport> is, so a change here is precisely a remount and a fresh GetReport — which
     * is the only thing that will call handleReportStateChange again to lower the load panel. Clicks
     * that leave the level alone (unticking Lot still resolves to Lot) correctly do neither.
     */
    const previousLevel = useRef(reportLevel);

    useEffect(() => {
        if (previousLevel.current === reportLevel) {
            return;
        }

        previousLevel.current = reportLevel;
        clearLevelDependentState();
        beginReportLoad();
    }, [reportLevel, clearLevelDependentState, beginReportLoad]);

    /**
     * Required: FormField calls onFormLoad() in fetchData's `finally` with no guard and no default
     * (FormField.js:256), so omitting it throws "onFormLoad is not a function" and the parameters panel
     * never renders.
     *
     * Unlike the sibling screens there are no static select options to inject — all three fields are
     * checkboxes — but the cascade's disabled state does have to be applied once the widgets exist.
     * FormField calls this BEFORE React has committed the child Fields, so formRefs is still empty at
     * that instant; hence the same 100ms shape the siblings use.
     */
    const handleFormLoad = useCallback(() => {
        setTimeout(() => applyCascadeEnablement(formFields), 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Belt-and-braces re-apply, mirroring the sibling screens: onFormLoad fires once per FormField
    // mount, and the dynamic-form editor's save/discard path remounts the fields without re-firing it.
    useEffect(() => {
        applyCascadeEnablement(formFields);
    }, [formFields, applyCascadeEnablement]);

    /**
     * The checkbox check both buttons run before they post. Shows the same red toast the server would
     * have, without spending a round trip to be told what the screen already knows.
     */
    const selectionIsUsable = useCallback(() => {
        if (isValidSelection(formFields)) {
            return true;
        }

        notify({ message: MESSAGES.PURGE_INVALID_RECORD_SELECTION, width: 450 }, "error", 5000);
        return false;
    }, [formFields]);

    const handlePreview = useCallback(async () => {
        if (!selectionIsUsable()) {
            return;
        }

        // Brings the popup (and with it the grid) into the tree now, a commit before `visible` flips
        // below — see previewMounted. Everything the grid does on mount is legitimate from here on: the
        // user has asked to see it.
        setPreviewMounted(true);

        // Commits any in-progress filter cell first, so Preview matches what the user typed.
        const filterRows = await readFilterRows();

        setBusy(BUSY.PREVIEWING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMPurgeNeverUsedRecords.GetRecords}`,
                JSON.stringify(buildRequest(filterRows)),
                postOptions()
            );

            const payload = response?.data;

            // DEV-5355 F2. A row array is the ONLY shape treated as success; anything else is an error,
            // whatever the envelope claims.
            //
            // Two failure modes reach here as HTTP 200. One is a refusal decided BEFORE the query runs —
            // an invalid record selection, a filter naming a column this level cannot reach, an unusable
            // value — which arrives as Success:false with no Resultdata. The other fails DURING the
            // query, and that is the one a Success-only check misses. Requiring the array closes both.
            if (payload?.Success === false || !Array.isArray(payload?.Resultdata)) {
                setPreview({ rows: NO_PREVIEW_ROWS, visible: false, hasRun: false });
                notify({ message: payload?.Message || MESSAGES.ERROR, width: 450 }, "error", 5000);
                return;
            }

            // An empty array IS legitimate — the grid says so via noDataText.
            setPreview({ rows: payload.Resultdata, visible: true, hasRun: true });
        } catch (error) {
            setPreview({ rows: NO_PREVIEW_ROWS, visible: false, hasRun: false });
            notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [selectionIsUsable, readFilterRows, buildRequest, postOptions]);

    /**
     * Start: ask, then run. One POST.
     *
     * Both questions are asked here rather than server-side, because both are acknowledgements and
     * neither needs the database: the first is unconditional, and the second only needs to know whether
     * the user entered any criteria — which the committed filter rows already tell us. Stopping at the
     * first No matches the Delphi, which asked sequentially and abandoned the run on the first refusal.
     *
     * Titled MESSAGES.CONFIRM, as the Delphi dialog is (ab_messagedlgEx with mtConfirmation), rather
     * than the page title the sibling screens pass.
     */
    const handleStart = useCallback(async () => {
        // Before the confirmations: no point asking someone to acknowledge a destructive run that could
        // not proceed anyway.
        if (!selectionIsUsable()) {
            return;
        }

        // Read BEFORE the confirmations, not after: the empty-filter question has to see the COMMITTED
        // rows, or a value the user typed without tabbing out looks like no filter at all and they get
        // asked to delete everything.
        const filterRows = await readFilterRows();

        if ((await confirm(MESSAGES.PURGE_MASTER_RECORDS_CONFIRM, MESSAGES.CONFIRM)) !== true) {
            return;
        }

        if (!hasFilterCriteria(filterRows)
            && (await confirm(MESSAGES.PURGE_NO_FILTER_CRITERIA_CONFIRM, MESSAGES.CONFIRM)) !== true) {
            return;
        }

        setBusy(BUSY.PURGING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMPurgeNeverUsedRecords.Start}`,
                JSON.stringify(buildRequest(filterRows)),
                postOptions()
            );

            const payload = response?.data;
            const data = payload?.Resultdata;

            if (data == null || (data.RecordsPurged ?? 0) <= 0) {
                notify(
                    { message: payload?.Message || MESSAGES.NO_RECORD_FOUND_RANGE, width: 450 },
                    "error",
                    5000
                );
                return;
            }

            // Green, and the rows the user was looking at are gone — so drop the stale preview rather
            // than leave a grid full of records that no longer exist.
            notify({ message: payload.Message, width: 450 }, "success", 5000);
            setPreview({ rows: NO_PREVIEW_ROWS, visible: false, hasRun: false });
        } catch (error) {
            notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [selectionIsUsable, readFilterRows, buildRequest, postOptions]);

    const handleReset = useCallback(() => {
        const next = initialFormFields();
        const levelChanges = resolvePurgeLevel(next) !== resolvePurgeLevel(formFields);

        setFormFields(next);
        clearLevelDependentState();
        beginReportLoad();

        // Re-fetches the field definitions, rebuilds the filter store and re-fires onReportStateChange
        // with a fresh array (wbPurgeUnusedItems.Reset, uWMD0120.pas btnResetFilterClick).
        //
        // ONLY when the level is unchanged. If Reset also changes the level, the `key` on
        // <CommonReport> changes and the remount does the fetch — calling loadReport as well would hit
        // the OUTGOING instance (reportRef still points at it until the new one mounts), and that
        // instance's onReportStateChange would lower the load panel before the new catalogue arrived.
        if (!levelChanges && reportRef.current && typeof reportRef.current.loadReport === "function") {
            reportRef.current.loadReport();
        }
    }, [formFields, clearLevelDependentState, beginReportLoad]);

    return (
        <>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={isBusy}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                message={loadPanelMessage}
            />

            {/*
                Delphi's grpMain is a centred bold title band above the where-builder. In the web app the
                page title lives in the toolbar, so the band collapses into this header rather than being
                reproduced as a separate strip.
            */}
            <div className="row">
                <div className="col-md-12 main-page">
                    <Toolbar className="header">
                        <Item location="before">
                            <div className="title">
                                <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>{PAGE_TITLE}</h6>
                            </div>
                        </Item>

                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Start} type="default" onClick={handleStart} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Preview} onClick={handlePreview} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Reset} onClick={handleReset} disabled={isBusy} />
                        </Item>
                    </Toolbar>
                </div>
            </div>

            <div className="pnu-page">
                {/* The Delphi criteria memo (edtMemo), which sits at the top of the form under the title. */}
                <div className="op-help pnu-help">{HELP_TEXT}</div>

                {/*
                    Record Range comes first, directly under the criteria memo, exactly as the Delphi
                    form lays it out — wbPurgeUnusedItems fills the middle of the form and pnlBottom sits
                    under it.

                    `key` is MANDATORY, not a fallback. CommonReport's componentDidUpdate compares
                    prevProps.Name against this.props.Name (capital N) while loadReport reads
                    this.props.name — the reload-on-name-change guard is dead code and never fires
                    (CommonReport.js:112 vs :448). Remounting on the level is what re-runs loadReport
                    with the new catalogue and a fresh FilterWhereInfoList, which is exactly what every
                    checkbox click did in Delphi (DataSet.Close; Reset; DataSet := DM.qryWM_X).
                */}
                <div className="op-filter">
                    <CommonReport
                        key={report.name}
                        name={report.name}
                        fullName={report.fullName}
                        onReportStateChange={handleReportStateChange}
                        innerRef={reportRef}
                    />
                </div>

                {/*
                    Delphi's pnlBottom, below the Record Range: grpBottomLeft (alLeft, Width 305) holds
                    edtBackMemo — the red memo — and grpBottomRight (alClient, Width 483) holds the
                    caption and the three checkboxes, both 120px tall. Same order, same side-by-side
                    arrangement, same place on the page.

                    Worth knowing while reading this: ticking a box here clears the Record Range above,
                    because each level has its own field catalogue and the Delphi did the same. That is
                    why the filter starts empty again after a level change.
                */}
                <div className="pnu-selection-row">
                    <div className="pnu-backup-warning">{BACKUP_WARNING_TEXT}</div>

                    <div className="op-parameters pnu-parameters">
                        {/*
                            hasOnlyOneColumn because there is exactly one group. Without it FormField
                            gives the group col-md-4, i.e. a third of the page, for three checkboxes.
                        */}
                        <FormField
                            currrentUser={currentUser}
                            layoutId={LAYOUT_ID}
                            layoutName={LAYOUT_NAME}
                            defaultFormSchema={ParametersLayout}
                            formFields={formFields}
                            setformFields={setFormFields}
                            DYNAMICFORMSETTING={dynamicFormSetting}
                            formRefs={formRefs}
                            onFormLoad={handleFormLoad}
                            handleFieldValueChange={handleFieldChange}
                            hasOnlyOneColumn={true}
                        />
                    </div>
                </div>
            </div>

            {/*
                Gated on previewMounted, not merely hidden with visible=false. A rendered-but-hidden popup
                still mounts OperationResultGrid, and that mount is what fires GetGridLayouts and the
                StateStoring customLoad — plus a SaveUpdateGridLayout when the level's column set
                changes. Ticking a checkbox should cost one request (GetReport), so until Preview is
                pressed there is no grid in the tree to make any of those calls.
            */}
            {previewMounted && (
                <Popup
                    visible={preview.visible}
                    onHiding={hidePreview}
                    hideOnOutsideClick={true}
                    showCloseButton={true}
                    title={PREVIEW_TITLE}
                    width="94vw"
                    height="76vh"
                    wrapperAttr={PREVIEW_POPUP_ATTR}
                >
                    {/*
                        The grid FILLS the popup rather than being given a computed height — see
                        .op-preview-popup in the shared operationScreen.css.

                        No ScrollView wrapper: the grid owns both scroll axes, and an outer scroller would
                        give it an unbounded width to lay out against, killing horizontal scrolling.
                    */}
                    <div className="op-preview-body">
                        <div className="op-preview-grid">
                            {/*
                                Deliberately NO `key` prop, but a gridKey PER LEVEL — the two are separate
                                decisions and it is worth not conflating them.

                                No `key`: a key per level remounted the grid on every checkbox click, which
                                mounted GridLayoutManager and fired GetGridLayouts. The columns prop varying
                                is enough — devextreme-react re-creates the Column children when it changes.

                                A gridKey per level: the three column sets differ (5 / 6 / 7), and
                                StateStoring persists the whole column array, so one shared key would restore
                                a Sublot-shaped layout onto Item level's five columns. See PREVIEW_GRID_KEY.
                                It costs nothing now because this whole popup is gated on previewMounted and
                                every level change unmounts it, so the key is only ever read on a mount the
                                user asked for by pressing Preview.
                            */}
                            <OperationResultGrid
                                rows={preview.rows}
                                columns={previewColumns(reportLevel)}
                                keyExpr={PREVIEW_KEY_EXPR}
                                noDataText={preview.hasRun ? MESSAGES.PURGE_NO_RECORDS_FOUND : ""}
                                height="100%"
                                gridRef={previewGrid}
                                gridKey={PREVIEW_GRID_KEY[reportLevel]}
                                currentLayout={previewLayout}
                                setCurrentLayout={setPreviewLayout}
                                accessToken={accessToken}
                            />
                        </div>

                        {/*
                            The row count, as TLookupForm shows it under its grid (SearchFm.pas lbInfo).
                            Counted from preview.rows, i.e. the whole result set — NOT the grid's current page
                            or its filter-row state. Hidden at zero rows: noDataText already covers that.
                        */}
                        {preview.rows.length > 0 && (
                            <div className="op-preview-footer">
                                {MESSAGES.PREVIEW_RECORDS_MATCHING_FORMAT.replace("{0}", preview.rows.length)}
                            </div>
                        )}
                    </div>
                </Popup>
            )}
        </>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(PurgeNeverUsedRecords);
